import type { DashboardNotice } from "../lib/notices/types";

export type DeadlineFilter = "all" | "7" | "30" | "open" | "closed";
export type ChangeFilter = "all" | "new" | "changed" | "bookmarked";
export type NoticeSort = "deadline" | "fit" | "latest";

export interface DashboardFilters {
  query: string;
  category: string;
  region: string;
  source: string;
  eligibility: string;
  fit: string;
  deadline: DeadlineFilter;
  change: ChangeFilter;
}

export const DEFAULT_FILTERS: DashboardFilters = {
  query: "",
  category: "all",
  region: "all",
  source: "all",
  eligibility: "all",
  fit: "all",
  deadline: "all",
  change: "all",
};

const REGION_NAMES = [
  "서울",
  "경기",
  "인천",
  "강원",
  "충북",
  "충남",
  "대전",
  "세종",
  "전북",
  "전남",
  "광주",
  "경북",
  "경남",
  "대구",
  "울산",
  "부산",
  "제주",
] as const;

export function noticeRegion(notice: DashboardNotice): string {
  if (notice.region && notice.region !== "미확인") return notice.region;
  const searchable = [
    notice.title,
    notice.summary,
    notice.eligibilityText,
    notice.agency,
  ].join(" ");
  return REGION_NAMES.find((region) => searchable.includes(region)) ?? "전국";
}

function includesNormalized(value: string, query: string): boolean {
  return value.normalize("NFKC").toLocaleLowerCase("ko-KR").includes(query);
}

function isNewNotice(notice: DashboardNotice): boolean {
  const firstSeen = Date.parse(notice.firstSeenAt);
  return Number.isFinite(firstSeen) && Date.now() - firstSeen <= 7 * 86_400_000;
}

export function filterNotices(
  notices: readonly DashboardNotice[],
  filters: DashboardFilters,
): DashboardNotice[] {
  const query = filters.query.normalize("NFKC").trim().toLocaleLowerCase("ko-KR");

  return notices.filter((notice) => {
    if (
      query &&
      ![
        notice.title,
        notice.agency,
        notice.supervisingMinistry,
        notice.summary,
      ].some((value) => includesNormalized(value, query))
    ) {
      return false;
    }
    if (filters.category !== "all" && notice.category !== filters.category) return false;
    if (filters.region !== "all" && noticeRegion(notice) !== filters.region) return false;
    if (filters.source !== "all" && !notice.sourceNames.includes(filters.source)) return false;
    if (
      filters.eligibility !== "all" &&
      notice.evaluation?.eligibilityStatus !== filters.eligibility
    ) {
      return false;
    }
    if (filters.fit !== "all" && notice.evaluation?.fitGrade !== filters.fit) return false;

    if (notice.sourceMissing && filters.deadline !== "all") return false;
    if (filters.deadline === "7" && (notice.dday === null || notice.dday < 0 || notice.dday > 7)) {
      return false;
    }
    if (filters.deadline === "30" && (notice.dday === null || notice.dday < 0 || notice.dday > 30)) {
      return false;
    }
    if (filters.deadline === "open" && !["접수중", "접수예정"].includes(notice.status)) {
      return false;
    }
    if (filters.deadline === "closed" && notice.status !== "마감") return false;

    if (filters.change === "new" && !isNewNotice(notice)) return false;
    if (
      filters.change === "changed" &&
      !notice.changes.some((change) => change.changeType !== "new")
    ) return false;
    if (filters.change === "bookmarked" && !notice.user?.bookmarked) return false;
    return true;
  });
}

export function sortNotices(
  notices: readonly DashboardNotice[],
  sort: NoticeSort,
): DashboardNotice[] {
  return [...notices].sort((left, right) => {
    if (sort === "fit") {
      const scoreDifference =
        (right.evaluation?.fitScore ?? -1) - (left.evaluation?.fitScore ?? -1);
      if (scoreDifference !== 0) return scoreDifference;
    }

    if (sort === "latest") {
      const latestDifference =
        Date.parse(right.publishedAt ?? right.firstSeenAt) -
        Date.parse(left.publishedAt ?? left.firstSeenAt);
      if (latestDifference !== 0 && Number.isFinite(latestDifference)) return latestDifference;
    }

    if (sort === "deadline") {
      const leftDay = left.dday === null || left.dday < 0 ? Number.MAX_SAFE_INTEGER : left.dday;
      const rightDay = right.dday === null || right.dday < 0 ? Number.MAX_SAFE_INTEGER : right.dday;
      if (leftDay !== rightDay) return leftDay - rightDay;
    }

    return left.title.localeCompare(right.title, "ko-KR");
  });
}

export function priorityNotices(notices: readonly DashboardNotice[], limit = 3): DashboardNotice[] {
  return [...notices]
    .filter(
      (notice) =>
        !notice.sourceMissing &&
        notice.status !== "마감" &&
        notice.evaluation?.eligibilityStatus !== "불충족" &&
        notice.evaluation?.fitGrade !== "사실상 부적합",
    )
    .sort((left, right) => {
      const scoreDifference =
        (right.evaluation?.fitScore ?? -1) - (left.evaluation?.fitScore ?? -1);
      if (scoreDifference !== 0) return scoreDifference;
      return (left.dday ?? Number.MAX_SAFE_INTEGER) - (right.dday ?? Number.MAX_SAFE_INTEGER);
    })
    .slice(0, limit);
}
